import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useLocation } from 'react-router-dom';

import { ItemCardCountdown } from '@/components/GridItemCard/Card/components/ItemCardCountdown/ItemCardCountdown';
import { WeakRouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummarySellerId, getItemSummarySellerName } from '@/redux/modules/item/summary/itemSummary.selectors';
import { ItemCardNavigationTrigger } from '@/utils/amplitude/analytics';
import { getSellerUrl, isMyBidsPage, isSavedItemsPage } from '@/utils/urls';

export type CardInfoProps = {
    hasNewSimilarItemsIconCTA?: boolean;
    isMobile: boolean;
    isOpenItem: boolean;
    itemId: number;
    onNavigate?: (trigger: ItemCardNavigationTrigger) => void;
};

export const CardInfo = ({
    hasNewSimilarItemsIconCTA = false,
    isMobile,
    isOpenItem,
    itemId,
    onNavigate,
}: CardInfoProps) => {
    const { pathname } = useLocation();
    const sellerId = useAppSelector((state) => getItemSummarySellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemSummarySellerName(state, itemId));

    // Displays Card Info in Timed Plus item cards on Search, and My Items Pages.
    // On My Items Pages, we show the auction house name because there are no star ratings.
    // On Search Pages, we hide the auction house name because the star ratings already show it.
    const shouldShowAuctionHouseName = isMyBidsPage(pathname) || isSavedItemsPage(pathname);

    return (
        <section
            className="flex items-center justify-between gap-xs [grid-area:iteminfo]"
            data-testid="timedPlusMyItemsCardInfo"
        >
            <span className="max-[375px]:w-auto leading-[18px] xsMax:w-full">
                <ItemCardCountdown
                    className={cn({ 'text-xs smMin:text-sm': hasNewSimilarItemsIconCTA })}
                    hasNewSimilarItemsIconCTA={hasNewSimilarItemsIconCTA}
                    itemId={itemId}
                    onNavigate={() => onNavigate?.(ItemCardNavigationTrigger.CountdownClick)}
                />
            </span>
            {shouldShowAuctionHouseName && isOpenItem && !isMobile && (
                <WeakRouteLink
                    className="group max-w-[140px] truncate text-right leading-[18px] no-underline xsMax:min-w-[94px] xsMax:max-w-[94px]"
                    data-testid="auction-house-name-container"
                    onClick={() => onNavigate?.(ItemCardNavigationTrigger.HouseNameClick)}
                    to={getSellerUrl(sellerId, sellerName)}
                >
                    <BodySecondary
                        className="group-hover:!text-link-primary group-hover:!underline"
                        color="secondary"
                    >
                        {sellerName}
                    </BodySecondary>
                </WeakRouteLink>
            )}
        </section>
    );
};
